import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import { fetchInvitationMailPreview } from '@/features/invitations/api';

/** 送信後に画面へ返す結果（受諾リンクはこのときにしか手に入らない / D-153） */
export interface InvitationSendResult {
  acceptUrl: string | null;
  mailSent: boolean;
}

/**
 * 招待メールの送信前確認（D-153）。
 * 「誰に・どんな文面が送られるか」を出してから送る。会員・理事講師／サクセス担当・
 * 事務局アカウントの3つの招待画面で共通に使う。
 *
 * SMTPが未設定の間（`SMTP_MOCK=true`）は**送ったことにせず**、
 * 「実際には送られない・受諾リンクを直接お伝えください」と正直に出す。
 * 送信後は同じモーダルで結果（受諾リンク）を見せてから画面を移る。
 */
export function InvitationMailConfirmModal({
  open,
  email,
  expiresInDays = 14,
  busy,
  result,
  onSend,
  onCancel,
  onDone,
}: {
  open: boolean;
  email: string;
  expiresInDays?: number;
  busy: boolean;
  /** 送信が終わったら渡す。渡すと結果表示に切り替わる */
  result?: InvitationSendResult | null;
  onSend: () => void;
  onCancel: () => void;
  /** 結果を確認して閉じたとき（呼び出し側で一覧・詳細へ移る） */
  onDone: () => void;
}): ReactElement {
  const [copied, setCopied] = useState(false);
  const preview = useQuery({
    queryKey: ['invitation-mail-preview', email, expiresInDays],
    queryFn: () => fetchInvitationMailPreview(email, expiresInDays),
    enabled: open && !result && email.trim().length > 0,
  });

  const willSend = preview.data?.will_send ?? false;

  // --- 送信後: 結果を見せる ---
  if (result) {
    return (
      <Modal
        open={open}
        title={result.mailSent ? '招待メールを送信しました' : '招待を登録しました'}
        onClose={onDone}
        wide
        footer={
          <button
            type="button"
            onClick={onDone}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
          >
            閉じる
          </button>
        }
      >
        {result.mailSent ? (
          <Notice kind="success" dismissible={false}>
            {email} 宛に招待メールを送信しました。
          </Notice>
        ) : (
          <Notice kind="warning" dismissible={false}>
            <strong>メールは送っていません。</strong>
            いまはメールの送信が無効なため、招待の登録だけを行いました。
            下の受諾用リンクを、{email} のご本人に直接お伝えください。
          </Notice>
        )}
        {result.acceptUrl && (
          <div className="mt-3">
            <p className="mb-1 text-wptext-muted">
              受諾用リンク（<strong>この画面を閉じると二度と表示できません</strong>）
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 break-all rounded-sm border border-wpborder bg-wpcanvas/60 p-2">
                {result.acceptUrl}
              </code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(result.acceptUrl ?? '');
                  setCopied(true);
                }}
                className="btn-secondary"
              >
                {copied ? 'コピーしました' : 'コピー'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    );
  }

  // --- 送信前: 内容を確認してもらう ---
  return (
    <Modal
      open={open}
      title="この内容で招待を送ります"
      onClose={onCancel}
      wide
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="btn-secondary"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={busy || preview.isPending || preview.isError}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {busy ? '処理中…' : '送信する'}
          </button>
        </>
      }
    >
      {preview.isPending && <p className="text-wptext-muted">読み込み中…</p>}
      {preview.isError && (
        <Notice kind="error" dismissible={false}>
          送信内容の確認に失敗しました。時間をおいてもう一度お試しください。
        </Notice>
      )}
      {preview.data && (
        <>
          {!willSend && (
            <Notice kind="warning" dismissible={false}>
              <strong>いまはメールの送信が無効になっています。</strong>
              「送信する」を押すと招待は作られますが、
              <strong>メールは実際には送られません</strong>。
              送信後に表示される受諾用リンクを、ご本人に直接お伝えください。
              （本番のメール設定を入れると、そのまま送信されるようになります）
            </Notice>
          )}
          <dl className="flex flex-col gap-3">
            <div>
              <dt className="text-wptext-muted">宛先</dt>
              <dd className="text-wptext-strong">{preview.data.to}</dd>
            </div>
            <div>
              <dt className="text-wptext-muted">件名</dt>
              <dd className="text-wptext-strong">{preview.data.subject}</dd>
            </div>
            <div>
              <dt className="text-wptext-muted">本文</dt>
              <dd>
                <pre className="mt-1 whitespace-pre-wrap rounded-sm border border-wpborder bg-wpcanvas/60 p-3 font-sans text-wptext-strong">
                  {preview.data.body}
                </pre>
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-wptext-muted">
            この宛先が、そのまま<strong>ログインに使うメールアドレス</strong>になります。
            受け取ったご本人がリンクを開いてパスワードを決めると、アカウントができます。
          </p>
        </>
      )}
    </Modal>
  );
}
